/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { SCHEMA_VALIDATION_ERROR_TYPE, ValidatorString } from "@adapptio/lib-blueprint";
import { BEM } from "@adapptio/lib-components";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { Form } from "@adapptio/lib-editor-ui";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDeepCompareMemo } from "use-deep-compare";
import { TDialogComponent } from "../../../dialogs/Dialog";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../../ui/components/ActionButton";
import { DialogButtons } from "../../../ui/components/Dialogs/DialogButtons";
import { DialogContent } from "../../../ui/components/Dialogs/DialogContent";
import { DialogHeader } from "../../../ui/components/Dialogs/DialogHeader";
import { DIALOG_SIZE, DialogPanel } from "../../../ui/components/Dialogs/DialogPanel";
import { FieldRow } from "../../../ui/components/Fields/FieldRow";
import { FieldText } from "../../../ui/components/Fields/FieldText";
import { IResourceListProps, IResourceNode, ResourceList } from "../../../ui/components/ResourceList";
import { CreateReadmeDialog } from "./CreateReadmeDialog";
import { useFieldState, useStateWithEvent } from "../../../ui/hooks";
import { isDefined } from "@adapptio/lib-shared";

const allowedResourceTypes: RESOURCE_TYPES[] = [ RESOURCE_TYPES.DIRECTORY ];

export const CreateReadmeDialogComponent: TDialogComponent<CreateReadmeDialog> = ({ dialog }) => {
	const baseClass = BEM("edt-module-readme-create-dialog");

	const params = useMemo(() => dialog.getParams(), []);
	const hideDirSelector = isDefined(params.basePath);

	const isBeingSaved = useStateWithEvent(dialog.onSaveStateChange, () => dialog.isBeingSaved());

	const [ selectedParentNode, setSelectedParentNode ] = useState<IResourceNode>(null);

	const selectResource = useCallback<IResourceListProps["onSelect"]>((selectedNode) => {
		setSelectedParentNode(selectedNode);
	}, []);

	const basePath = useDeepCompareMemo(() => {
		if (isDefined(params.basePath)) {
			return params.basePath;
		}

		if (selectedParentNode && !selectedParentNode.isRoot) {
			return normalizeResourcePath(selectedParentNode.basePath, selectedParentNode.name);
		}
		return "";
	}, [ selectedParentNode ]);

	const [ fieldState, setFieldState ] = useFieldState(
		{
			name: {
				defaultValue: "",
				validators: [
					ValidatorString({ required: true, min: 1 }),
					(value: string) => {
						if (dialog.checkIfBlueprintExists(basePath, value)) {
							return [
								{
									type: SCHEMA_VALIDATION_ERROR_TYPE.DUPLICATE_IDENTIFIER,
									message: "A file with this name already exists.",
									metaData: {}
								}
							];
						} else {
							return [];
						}
					}
				]
			}
		},
		[ dialog ]
	);

	useEffect(() => {
		if (fieldState.name.value.length) {
			setFieldState.name(fieldState.name.value);
		}
	}, [ basePath ]);

	const handleClose = useCallback(() => {
		dialog.close({
			resourceId: null
		});
	}, [ dialog ]);

	const handleCreate = useCallback(() => {
		dialog.create({
			basePath: basePath,
			name: fieldState.name.value
		});
	}, [ dialog, fieldState, basePath ]);

	return (
		<DialogPanel size={DIALOG_SIZE.SMALL} className={baseClass.toClassName()}>
			<Form onSubmit={handleCreate} noValidate>
				<DialogHeader title="Create Readme" icon="mdi/file-document" onClose={handleClose} />
				<DialogContent>
					<FieldRow>
						<FieldText
							value={fieldState.name.value}
							validationErrors={fieldState.name.validationErrors}
							placeholder="Readme Name"
							grow={true}
							onChange={setFieldState.name}
						/>
					</FieldRow>
					{!hideDirSelector && (
						<label className={baseClass.element("field-row-resource-list").toClassName()}>
							<ResourceList
								onSelect={selectResource}
								labelSearch="Select target directory"
								allowedResourceTypes={allowedResourceTypes}
								selectedId={selectedParentNode?.id ?? "__root__"}
							/>
						</label>
					)}
				</DialogContent>
				<DialogButtons>
					<ActionButton
						label="Cancel"
						icon="mdi/close"
						style={ACTION_BUTTON_STYLE.DEFAULT}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleClose}
						disabled={isBeingSaved}
					/>
					<ActionButton
						label="Create"
						icon="mdi/check"
						style={ACTION_BUTTON_STYLE.PRIMARY}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleCreate}
						disabled={
							fieldState.name.validationErrors.length > 0 ||
							fieldState.name.value === "" ||
							isBeingSaved
						}
						isLoading={isBeingSaved}
					/>
				</DialogButtons>
			</Form>
		</DialogPanel>
	);
};
